import { useMemo } from 'react';

import ProgressBar from '@ramonak/react-progress-bar';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '../../components/skeleton';
import { cn } from '../../lib/utils';
import { OrganisationUsage } from '@/api/types/backend-dto-types';
import { TooltipIcon } from '@/new/components/tooltip-icon';

const Label = ({ text }: { text: string }) => (
  <p className="text-xs font-medium tracking-wider text-gray-500 uppercase">{text}</p>
);

export function CreditUsage({
  periodCreditUsage,
  isLoading,
}: {
  periodCreditUsage: OrganisationUsage['periodCreditUsage'];
  // Distinguishes "still fetching" (skeleton) from "genuinely no data" — see WEX-1700.
  isLoading?: boolean;
}) {
  const { i18n } = useTranslation();

  const totalCreditsUsed = periodCreditUsage?.used ?? 0;

  const usagePercentage = useMemo(() => {
    if (!periodCreditUsage?.maxCredits) return 0;
    return (periodCreditUsage.used / periodCreditUsage.maxCredits) * 100;
  }, [periodCreditUsage]);

  if (isLoading) {
    return (
      <div className="w-full">
        <Skeleton className="mt-4 block h-9 w-40 bg-gray-100" />
        <Skeleton className="mt-4 block h-2 w-full rounded-full bg-gray-100" />
      </div>
    );
  }

  // The org has no usage to report on at all. Usage-based orgs normally receive a
  // zero-valued object, so this is a defensive fallback.
  if (!periodCreditUsage) {
    return <p className="text-sm text-gray-500">No credit usage recorded for this period.</p>;
  }

  // The limit and billing-cycle dates only exist when an active billing plan is configured
  // and the default (unfiltered) view is shown.
  const hasLimit = periodCreditUsage.maxCredits !== undefined;
  const isOverLimit = hasLimit && totalCreditsUsed > periodCreditUsage.maxCredits!;

  return (
    <div className="w-full">
      <div className="flex items-start justify-between gap-2">
        <Label text="Total Credit Usage" />

        <div className="flex flex-col items-end gap-1">
          {periodCreditUsage.startDate && periodCreditUsage.endDate && (
            <p className="animate-in fade-in text-xs text-gray-500">
              Billing cycle{' '}
              <span className="font-medium text-gray-700">
                {new Intl.DateTimeFormat(i18n.language).format(new Date(periodCreditUsage.startDate))} –{' '}
                {new Intl.DateTimeFormat(i18n.language).format(new Date(periodCreditUsage.endDate))}
              </span>
            </p>
          )}
        </div>
      </div>

      <div className="mt-3 flex items-baseline gap-1.5">
        <span
          className={cn(
            'animate-in fade-in text-3xl font-semibold tracking-tight tabular-nums',
            isOverLimit ? 'text-red-600' : 'text-gray-900',
          )}
        >
          {new Intl.NumberFormat(i18n.language).format(totalCreditsUsed)}
        </span>

        {periodCreditUsage.maxCredits && (
          <span className="inline-flex items-center gap-1 text-sm text-gray-500">
            / {new Intl.NumberFormat('en-US', { notation: 'compact' }).format(periodCreditUsage.maxCredits)} limit
            <TooltipIcon tooltipContent={`${((totalCreditsUsed / periodCreditUsage.maxCredits) * 100).toFixed(1)}%`} />
          </span>
        )}
      </div>

      {hasLimit && (
        <ProgressBar
          completed={usagePercentage}
          height={'8px'}
          borderRadius="9999px"
          bgColor={isOverLimit ? 'var(--color-red-500)' : 'var(--color-teal-500)'}
          baseBgColor="var(--color-gray-100)"
          className="mt-4 w-full"
          isLabelVisible={false}
          animateOnRender={true}
        />
      )}
    </div>
  );
}
